<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { theme } from "@/services/config";
import leave from "@/mixins/leave";
import { v4 as uuidv4 } from "uuid";
import fileUploadConfigService from "@/services/fileupload-config";

import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import InputTag from "@/components/ui/InputTag";
import { cloneDeep, isEqual } from "lodash";
import { VIRTUAL_INDICATION_TYPES } from "@/services/indication-filter";
import ImagesSelect from "@/components/ui/ImagesSelect.vue";
import LabSelect from "@/components/ui/LabSelect.vue";
import DiagnosisSelect from "@/components/ui/DiagnosisSelect.vue";
import MedicationSelect from "@/components/ui/MedicationSelect.vue";
import FinanciadorSelect from "@/components/ui/FinanciadorSelect.vue";
import MultiDatePicker from "@/components/ui/MultiDatePicker.vue";
import FormSelect from "@/components/ui/FormSelect.vue";
import FormRenderer from "@/components/ui/FormRenderer.vue";
import ProgramSelect from "@/components/ui/ProgramSelect.vue";
import RepeatMedicationModal from "./RepeatMedicationModal.vue";
import RepeatOrderModal from "./RepeatOrderModal.vue";
import {
  getIndicationForm,
  INDICATIONS_TYPES,
  RECETARIO_SOLIDARIO_DISABLED_FIELDS,
} from "@/services/constants";
import indicationService from "@/services/indication";
import { isValidMongoId } from "@/utils/validators";

// Datos del paciente que se precargan solos (algunos de forma asíncrona, como
// la obra social cuando llega la lista de financiadores). No cuentan como algo
// que el médico cargó al decidir si una indicación quedó sin guardar.
const PATIENT_PREFILL_FIELDS = [
  "patientEmail",
  "patientDni",
  "patientSocioNumber",
  "patientPlan",
  "patientFinanciadorName",
];

const getPrescriptionFieldName = (field) =>
  String(field?.label || field?.name || field?.friendlyName || "")
    .trim()
    .toLowerCase();

const getPrescriptionPatientData = (patient = {}) => {
  let patientData = patient || {};
  if (patient && patient.patient) {
    patientData = { ...patient, ...patient.patient };
  }

  return {
    email: patientData?.email || "",
    dni: patientData?.governmentId?.number || "",
    memberNumber:
      patientData?.medicalInsuranceObj?.number ||
      patientData?.medicalInsuranceNumber ||
      patientData?.medicalInsuranceObj?.publicInsurance?.publicId ||
      "",
  };
};

const buildPrescriptionForm = (patient = {}) => {
  const form = cloneDeep(getIndicationForm("medications"));
  const patientData = getPrescriptionPatientData(patient);

  const quantityField = form.find((field) => {
    const fieldName = getPrescriptionFieldName(field);
    return fieldName === "quantity" || fieldName === "cantidad";
  });
  if (quantityField && !quantityField.value) {
    quantityField.value = "1";
  }

  // Precarga mail
  const patientEmailField = form.find(
    (field) => getPrescriptionFieldName(field) === "patientemail"
  );
  if (patientEmailField) {
    patientEmailField.value = patientData.email;
  }

  // Precarga DNI
  const patientDniField = form.find(
    (field) => getPrescriptionFieldName(field) === "patientdni"
  );
  if (patientDniField) {
    patientDniField.value = patientData.dni;
  }

  // Precarga número de socio
  const patientSocioField = form.find(
    (field) =>
      getPrescriptionFieldName(field) === "patientsocionumber" ||
      getPrescriptionFieldName(field) === "patientmembernumber"
  );
  if (patientSocioField) {
    patientSocioField.value = patientData.memberNumber;
  }

  return form;
};

export default {
  name: "Indication",
  mixins: [leave],

  components: {
    Spinner,
    FileUpload,
    InputTag,
    DiagnosisSelect,
    MedicationSelect,
    FinanciadorSelect,
    ImagesSelect,
    LabSelect,
    MultiDatePicker,
    FormSelect,
    FormRenderer,
    ProgramSelect,
    RepeatMedicationModal,
    RepeatOrderModal,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    savedIndications: {
      type: Array,
      required: true,
    },
    appointment: {
      type: Object,
      default: null,
    },
    existingIndication: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "create",
      validator: (value) => ["create", "edit"].includes(value),
    },
    typeName: {
      type: String,
      default: null,
    },
    prefillValues: {
      type: Object,
      default: null,
    },
    patient: {
      type: Object,
    },
  },

  data() {
    return {
      theme,
      isLoading: false,
      isPosting: false,
      hasFiles: false,
      isFetchingIndication: false,
      usesExternalMedication: true,
      mustShowForm: false,
      isSaveDisabled: true,
      isAddNewDisabled: false,
      isSelected: false,
      isRepeatMedicationModalVisible: false,
      isRepeatOrderModalVisible: false,
      indications: [],
      isFormModalVisible: false,
      selectedForm: null,
      prefillData: null,
      isEditing: false,
      completedForms: {},
      selectedFormForRenderer: null,
      isFormRendererVisible: false,
      editingFormData: null,
      isEditingForm: false,
      selectedProgram: null,
      programForms: [],
      completedProgramForms: {},
      activeCollapse: [],
      prescriptionForm: buildPrescriptionForm(
        (typeof this.appointment?.patient === "object" &&
        this.appointment?.patient !== null
          ? this.appointment.patient
          : null) || this.patient
      ),
      prescriptionTouched: false,
      prescriptionCompleted: false,
      prescriptionDisabled: false,
      diagnosisTouched: false,
      publicDiagnosisTouched: false,
      isDraftRestored: false,
    };
  },

  async created() {
    // Valores con los que arranca cada indicación (precargas incluidas), para
    // saber si el médico escribió algo que todavía no guardó. No es reactivo.
    this.initialValuesByKey = {};

    if (this.mode !== "edit") {
      this.addIndication();
    }

    await this.getForms();

    this.$bus.$on("deleteIndicationLocal", (key) => {
      const index = this.indications.findIndex((i) => i.key === key);
      if (index !== -1) {
        this.indications.splice(index, 1);
      }
    });
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor", "getDiagnosisById"]),

    // Solo dentro de una atención: el borrador se identifica por turno. Los
    // formularios y programas guardan su estado aparte y no entran.
    isDraftEnabled() {
      return (
        this.mode !== "edit" &&
        !!this.appointment?._id &&
        !VIRTUAL_INDICATION_TYPES.includes(this.form.type)
      );
    },

    patientInsuranceName() {
      return (
        this.patient?.patient?.medicalInsuranceObj?.publicInsurance?.name || ""
      );
    },

    currentPatient() {
      const isAppointmentRoute =
        this.$route.name === "appointment" ||
        this.$route.path.includes("/appointment/");

      let patientId;

      if (isAppointmentRoute) {
        if (this.appointment && this.appointment.patient) {
          patientId = this.appointment.patient || this.appointment.patient._id;
        } else {
          console.warn("No patient found in appointment data");
          return null;
        }
      } else {
        patientId = this.$route.params.id;
        if (!patientId) {
          console.warn("No patient ID found in route params");
          return null;
        }
      }

      return {
        _id: patientId,
        governmentId: this.patient?.governmentId,
        bornAt: this.patient?.bornAt,
        firstName: this.patient?.firstName,
        lastName: this.patient?.lastName,
      };
    },
    actualIndications() {
      if (this.mode === "edit" && this.existingIndication) {
        return [this.prepareIndicationForEdit(this.existingIndication)];
      }
      return this.indications;
    },
  },

  watch: {
    indications: {
      handler(newVal) {
        if (newVal.length < 1 && this.mode !== "edit") {
          this.addIndication();
        }
        if (this.isDraftEnabled) {
          this.$bus.$emit("indication-draft-changed");
        }
      },
      deep: true,
    },
    prefillValues(newVal) {
      if (!newVal) return;
      const indication = this.indications[0];
      if (!indication) return;
      // Si ya se recuperó un borrador, la evolución guardada solo aporta el
      // id a actualizar: no pisa lo que el médico había escrito.
      for (const [key, value] of Object.entries(newVal)) {
        if (key === "_existingId") {
          this.$set(indication, "_backendId", value);
          if (!this.isDraftRestored) {
            this.$set(indication, "lockedAt", new Date());
          }
        } else if (
          !this.isDraftRestored &&
          indication.defaultValues?.[key] !== undefined
        ) {
          this.$set(indication.defaultValues[key], "value", value);
        }
      }
      if (!this.isDraftRestored) {
        this.snapshotInitialValues(indication);
      }
    },
    isSelected(val) {
      if (val) {
        this.diagnosisTouched = true;
        this.publicDiagnosisTouched = true;
      }
    },
  },

  methods: {
    getIndicationValues(indication) {
      return Object.entries(indication.defaultValues || {}).reduce(
        (values, [fieldKey, question]) => {
          if (!PATIENT_PREFILL_FIELDS.includes(fieldKey)) {
            values[fieldKey] = cloneDeep(question?.value);
          }
          return values;
        },
        {}
      );
    },

    snapshotInitialValues(indication) {
      this.initialValuesByKey[indication.key] =
        this.getIndicationValues(indication);
    },

    // Una precarga externa (p. ej. el diagnóstico elegido en la evolución) no
    // cuenta como algo que el médico escribió en esta indicación.
    updateInitialValue(indication, field, value) {
      const initialValues = this.initialValuesByKey[indication.key];
      if (!initialValues) return;
      const fieldKey = Object.keys(indication.defaultValues || {}).find(
        (key) => indication.defaultValues[key] === field
      );
      if (fieldKey !== undefined) initialValues[fieldKey] = cloneDeep(value);
    },

    // Indicaciones con datos cargados que no se confirmaron con "Guardar":
    // al finalizar la atención no se envían. Si ya se había guardado y se
    // reabrió con "Editar", se compara contra lo que quedó en el resumen.
    getUnsavedIndications() {
      if (this.mode === "edit") return [];
      return this.indications.filter((indication) => {
        if (indication.lockedAt) return false;
        const savedVersion = this.savedIndications.find(
          (saved) => saved.key === indication.key
        );
        const baseline = savedVersion
          ? this.getIndicationValues(savedVersion)
          : this.initialValuesByKey[indication.key];
        return (
          baseline && !isEqual(this.getIndicationValues(indication), baseline)
        );
      });
    },

    // Lo que hay que guardar en el borrador del turno: las indicaciones con
    // datos cargados (guardadas en el resumen o no) que todavía no se
    // enviaron. Las ya enviadas se vuelven a traer del servidor.
    getDraftState() {
      if (!this.isDraftEnabled) return [];
      return this.indications
        .filter((indication) => {
          const savedVersion = this.savedIndications.find(
            (saved) => saved.key === indication.key
          );
          if (savedVersion?.createdAt) return false;
          const initialValues = this.initialValuesByKey[indication.key];
          return (
            initialValues &&
            !isEqual(this.getIndicationValues(indication), initialValues)
          );
        })
        .map((indication) => ({
          key: indication.key,
          lockedAt: indication.lockedAt || null,
          _backendId: indication._backendId,
          values: Object.entries(indication.defaultValues || {}).reduce(
            (values, [fieldKey, question]) => {
              values[fieldKey] = cloneDeep(question?.value);
              return values;
            },
            {}
          ),
        }));
    },

    // Reconstruye las indicaciones de un borrador sobre un formulario nuevo,
    // con las mismas keys para que coincidan con las del resumen.
    restoreDraftState(items = []) {
      if (!this.isDraftEnabled || !items.length) return;

      const restored = items.map((item) => {
        const indication = this.createIndication();
        this.initialValuesByKey[item.key] =
          this.getIndicationValues(indication);
        indication.key = item.key;
        for (const [fieldKey, value] of Object.entries(item.values || {})) {
          if (indication.defaultValues?.[fieldKey] !== undefined) {
            indication.defaultValues[fieldKey].value = value;
          }
        }
        indication.lockedAt = item.lockedAt ? new Date(item.lockedAt) : null;
        if (item._backendId) indication._backendId = item._backendId;
        return indication;
      });

      this.indications = restored;
      this.isDraftRestored = true;
      this.isSelected = true;

      restored
        .filter((indication) => indication.lockedAt)
        .forEach((indication) => this.updateIndication(indication));
    },

    isDiagnosisField(question) {
      return question?.inputType === "diagnosisSelect";
    },

    isPublicDiagnosisField(question) {
      if (!question) return false;
      if (
        question.name === "publicDiagnosis" ||
        question.label === "publicDiagnosis"
      )
        return true;
      const friendlyName = String(question.friendlyName || "")
        .trim()
        .toLowerCase();
      return friendlyName === "diagnóstico manual";
    },

    getQuestionRules(indication, question) {
      const typesWithManualDiagnosis = [
        "images",
        "lab",
        "practices",
        "certificates",
      ];

      if (this.isDiagnosisField(question) && indication.type !== "evolution") {
        if (typesWithManualDiagnosis.includes(indication.type)) {
          return "";
        }
        return "required";
      }

      return question.required ? "required" : "";
    },

    isMedicationQuantityQuestion(indication, question) {
      const fieldName = String(
        question?.label || question?.name || question?.friendlyName || ""
      )
        .trim()
        .toLowerCase();

      return indication?.type === "medications" && fieldName === "cantidad";
    },

    hasInvalidMedicationQuantity(indication) {
      if (indication?.type !== "medications") return false;

      const defaultValues = indication?.defaultValues;
      const questions = Array.isArray(defaultValues)
        ? defaultValues
        : Object.values(defaultValues || {});

      const quantityQuestion = questions.find((question) =>
        this.isMedicationQuantityQuestion(indication, question)
      );

      if (!quantityQuestion) return false;

      return !/^[1-9]\d*$/.test(String(quantityQuestion.value || "").trim());
    },

    getSignatureAsBase64() {
      const doctor = this.$store.state.user;
      return doctor?.doctor?.signature || null;
    },

    // Formulario de indicaciones: los campos no tienen label, se comparan por referencia.
    isDisabledByRecetarioSolidario(indication, question) {
      const dv = indication.defaultValues || {};
      if (!dv.recetarioSolidario?.value) return false;
      return RECETARIO_SOLIDARIO_DISABLED_FIELDS.some(
        (key) => dv[key] === question
      );
    },

    // Formulario de prescripción del programa: los campos tienen label.
    isPrescriptionFieldDisabledByRecetarioSolidario(field) {
      const recetarioSolidario = this.prescriptionForm.find(
        (f) => f.label === "recetarioSolidario"
      );
      return (
        !!recetarioSolidario?.value &&
        RECETARIO_SOLIDARIO_DISABLED_FIELDS.includes(field.label)
      );
    },

    isPrescriptionQuantityField(field) {
      const fieldName = String(field?.name || field?.friendlyName || "")
        .trim()
        .toLowerCase();

      return fieldName === "quantity" || fieldName === "cantidad";
    },

    hasInvalidPrescriptionQuantity() {
      const quantityField = this.prescriptionForm.find((field) =>
        this.isPrescriptionQuantityField(field)
      );

      if (!quantityField) return false;

      return !/^[1-9]\d*$/.test(String(quantityField.value || "").trim());
    },

    hasInvalidPrescriptionMedicationSelection() {
      const medicationField = this.prescriptionForm.find(
        (field) => field.inputType === "medicationSelect"
      );

      if (!medicationField || !medicationField.required) return false;

      const medicationId = String(medicationField.value || "").trim();
      if (!medicationId) return true;

      return !this.$store.state.cacheMedications?.[medicationId];
    },

    preventInvalidQuantityKey(event) {
      if (["-", "+", "e", "E", ".", ","].includes(event.key)) {
        event.preventDefault();
      }
    },

    async savePrescriptionIndication() {
      this.prescriptionTouched = true;

      if (this.hasInvalidPrescriptionQuantity()) {
        this.$message.error("La cantidad debe ser mayor a 0.");
        return;
      }

      if (this.hasInvalidPrescriptionMedicationSelection()) {
        this.$message.error("Debe seleccionar un medicamento de la lista.");
        return;
      }

      try {
        this.isPosting = true;
        this.showLeaveGuard = false;

        const formData = new FormData();

        const prescription = {
          type: "medications",
          patientId: this.currentPatient._id,
          doctorId: this.$store.state.user?._id || null,
          programName: this.selectedProgram?.name || "",
          diagnosis: null,
          publicMedication: null,
        };

        // Obtener la firma del médico en base64
        const signatureBase64 = this.getSignatureAsBase64();
        if (signatureBase64) {
          prescription.doctorSignature = signatureBase64;
        }

        const fieldMap = {
          cantidad: "quantity",
          posologia: "posology",
          observaciones: "observations",
          "tratamiento prolongado": "isLongTerm",
          diagnostico: "diagnosis",
        };

        this.prescriptionForm.forEach((field) => {
          const val = field.value;
          if (val === undefined || val === null || val === "") return;

          const rawKey = field.label || field.name || field.friendlyName || "";
          const key = rawKey.toLowerCase().trim();
          const backendKey = fieldMap[key] || rawKey;

          if (field.inputType === "diagnosisSelect") {
            if (typeof val === "object") {
              prescription.diagnosis = {
                id: val._id || val.id,
                name: val.description || val.name || "",
              };
            } else {
              prescription.diagnosis = { id: val, name: "" };
            }

            if (formData.has("diagnosis")) formData.delete("diagnosis");
            formData.append(
              "diagnosis",
              JSON.stringify(prescription.diagnosis)
            );
            return;
          }

          if (field.inputType === "medicationSelect") {
            const medication = this.$store.state.cacheMedications?.[val];
            if (medication) {
              prescription.publicMedication = {
                drugId: medication._id,
                name: medication.name,
                brandName: medication.brandName,
                drug: medication.drug,
                presentation: medication.presentation,
                isPsychotropic: medication.isPsychotropic ?? false,
              };
              if (formData.has("publicMedication"))
                formData.delete("publicMedication");
              formData.append(
                "publicMedication",
                JSON.stringify(prescription.publicMedication)
              );
            }
            return;
          }

          let value = val;
          if (backendKey === "quantity") {
            // puede venir como string o array, lo normalizamos
            value = Number(Array.isArray(val) ? val[0] : val);
          } else if (backendKey === "isLongTerm") {
            value = val === "Sí" || val === true;
          } else if (typeof val === "object") {
            value = JSON.stringify(val);
          }

          if (formData.has(backendKey)) {
            formData.delete(backendKey);
          }
          formData.append(backendKey, value);
        });

        if (formData.has("type")) formData.delete("type");
        if (formData.has("patientId")) formData.delete("patientId");
        formData.append("type", prescription.type);
        formData.append("patientId", prescription.patientId);

        if (prescription.doctorId) {
          if (formData.has("doctorId")) formData.delete("doctorId");
          formData.append("doctorId", prescription.doctorId);
        }

        if (prescription.doctorSignature) {
          if (formData.has("doctorSignature"))
            formData.delete("doctorSignature");
          formData.append("doctorSignature", prescription.doctorSignature);
        }

        const segmentService = (await import("@/services/segment")).default;

        segmentService.track({ name: "Indication Created" });

        const created = await indicationService.createIndication(formData);
        this.prescriptionCompleted = true;
        this.prescriptionDisabled = true;

        if (this.hasFiles && this.$refs.fileUpload) {
          this.$refs.fileUpload.startUpload();
        }

        this.$message.success("Prescripción generada correctamente");
        this.activeCollapse = "";
        this.$bus.$emit("indication-created", created);
      } catch (error) {
        console.error("❌ Error al guardar la prescripción:", error);
      } finally {
        this.isPosting = false;
      }
    },
    uploadMediaEndpoint(indicationId) {
      return fileUploadConfigService.getIndicationFilesEndpoint(indicationId);
    },

    async triggerUpload(createdIndication) {
      console.log("triggerUpload in indications", createdIndication);

      const index = this.indications.findIndex(
        (indication) => indication.key === createdIndication.key
      );

      this.$set(this.indications, index, {
        ...this.indications[index],
        _id: createdIndication._id,
      });

      await Vue.nextTick();

      const refName = `fileUpload-${createdIndication.key}`;
      this.$refs[refName][0].startUpload();
    },

    updateIndication(indication, isDeleted = false) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), isDeleted);
    },

    openRepeatMedicationModal() {
      this.isRepeatMedicationModalVisible = true;
    },

    openRepeatOrderModal() {
      this.isRepeatOrderModalVisible = true;
    },

    // Builds a saved-looking medication indication from a past prescription and
    // sends it straight to the appointment summary via updateIndication() — the
    // same event submit() emits on success. Deliberately never touches
    // this.indications: the form fields in this component must stay empty, only
    // the summary (and its own "quitar" action) should reflect the repeat.
    async onRepeatMedicationSelected(sourceIndication) {
      const newIndication = {
        key: uuidv4(),
        ...cloneDeep(this.form),
        lockedAt: new Date(),
      };

      const patient =
        (typeof this.appointment?.patient === "object" &&
        this.appointment?.patient !== null
          ? this.appointment.patient
          : null) ||
        this.patient ||
        {};
      const patientEmail = patient?.email || "";
      const patientDni = patient?.governmentId?.number || "";
      const patientSocioNumber =
        patient?.patient?.medicalInsuranceObj?.number ||
        patient?.medicalInsuranceObj?.number ||
        "";
      const patientPlan =
        patient?.patient?.medicalInsuranceObj?.plan ||
        patient?.medicalInsuranceObj?.plan ||
        "";

      if (newIndication.defaultValues?.patientEmail) {
        newIndication.defaultValues.patientEmail.value = patientEmail;
      }
      if (newIndication.defaultValues?.patientDni) {
        newIndication.defaultValues.patientDni.value = patientDni;
      }
      if (newIndication.defaultValues?.patientSocioNumber) {
        newIndication.defaultValues.patientSocioNumber.value =
          patientSocioNumber;
      }
      if (newIndication.defaultValues?.patientPlan) {
        newIndication.defaultValues.patientPlan.value = patientPlan;
      }

      const publicMedication = sourceIndication.publicMedication;
      if (publicMedication?.drugId) {
        this.$store.dispatch("addMedication", {
          _id: publicMedication.drugId,
          name: publicMedication.name,
          presentation: publicMedication.presentation,
          brandName: publicMedication.brandName,
          drug: publicMedication.drug,
          isPsychotropic: publicMedication.isPsychotropic,
        });
        if (newIndication.defaultValues?.medication) {
          newIndication.defaultValues.medication.value =
            publicMedication.drugId;
        }
      }

      const diagnosis = sourceIndication.diagnosis;
      if (diagnosis?.id) {
        this.$store.dispatch("addDiagnoses", {
          _id: diagnosis.id,
          description: diagnosis.name,
        });
        if (newIndication.defaultValues?.diagnosis) {
          newIndication.defaultValues.diagnosis.value = diagnosis.id;
        }
      }

      const scalarFieldMap = {
        quantity: sourceIndication.quantity,
        dosage: sourceIndication.dosage ?? sourceIndication.posology,
        isLongTerm: sourceIndication.isLongTerm ? "Sí" : "No",
        generic: sourceIndication.generic ? "Sí" : "No",
        observations: sourceIndication.observations,
      };
      Object.entries(scalarFieldMap).forEach(([key, value]) => {
        if (
          newIndication.defaultValues?.[key] &&
          value !== undefined &&
          value !== null
        ) {
          newIndication.defaultValues[key].value = value;
        }
      });

      if (newIndication.defaultValues?.patientEmail) {
        let confirmedEmail;
        try {
          ({ value: confirmedEmail } = await this.$prompt(
            "La receta se va a enviar a este email. Podés editarlo antes de continuar.",
            "Email de envío",
            {
              confirmButtonText: "Aceptar",
              cancelButtonText: "Cancelar",
              inputValue: patientEmail,
              inputPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              inputErrorMessage: "El email no tiene un formato válido.",
            }
          ));
        } catch (err) {
          // El doctor canceló: no se agrega la prescripción repetida.
          return;
        }
        newIndication.defaultValues.patientEmail.value = confirmedEmail.trim();
      }

      this.updateIndication(newIndication);
    },

    // Same idea as onRepeatMedicationSelected but for lab/images orders:
    // these reference live catalog items (Lab/Images) instead of a
    // denormalized snapshot, so items deactivated since the order was made
    // get filtered out here (RepeatOrderModal already warns about them and
    // disables the select button when nothing active is left to copy).
    async onRepeatOrderSelected(sourceIndication) {
      const newIndication = {
        key: uuidv4(),
        ...cloneDeep(this.form),
        lockedAt: new Date(),
      };

      const patient =
        (typeof this.appointment?.patient === "object" &&
        this.appointment?.patient !== null
          ? this.appointment.patient
          : null) ||
        this.patient ||
        {};
      const patientEmail = patient?.email || "";
      const patientDni = patient?.governmentId?.number || "";
      const patientSocioNumber =
        patient?.patient?.medicalInsuranceObj?.number ||
        patient?.medicalInsuranceObj?.number ||
        "";
      const patientPlan =
        patient?.patient?.medicalInsuranceObj?.plan ||
        patient?.medicalInsuranceObj?.plan ||
        "";

      if (newIndication.defaultValues?.patientEmail) {
        newIndication.defaultValues.patientEmail.value = patientEmail;
      }
      if (newIndication.defaultValues?.patientDni) {
        newIndication.defaultValues.patientDni.value = patientDni;
      }
      if (newIndication.defaultValues?.patientSocioNumber) {
        newIndication.defaultValues.patientSocioNumber.value =
          patientSocioNumber;
      }
      if (newIndication.defaultValues?.patientPlan) {
        newIndication.defaultValues.patientPlan.value = patientPlan;
      }

      const isImages = this.form.type === "images";
      const sourceItemsKey = isImages ? "images" : "labs";
      const fieldName = isImages ? "image" : "labs";
      const addAction = isImages ? "addImages" : "addLabs";

      const activeItems = (sourceIndication[sourceItemsKey] || []).filter(
        (item) => item.isActive !== false
      );
      activeItems.forEach((item) => {
        this.$store.dispatch(addAction, item);
      });
      if (newIndication.defaultValues?.[fieldName]) {
        newIndication.defaultValues[fieldName].value = activeItems.map(
          (item) => item._id
        );
      }

      const diagnosis = sourceIndication.diagnosis;
      if (diagnosis?.id) {
        this.$store.dispatch("addDiagnoses", {
          _id: diagnosis.id,
          description: diagnosis.name,
        });
        if (newIndication.defaultValues?.diagnosis) {
          newIndication.defaultValues.diagnosis.value = diagnosis.id;
        }
      }

      const scalarFieldMap = {
        publicDiagnosis: sourceIndication.publicDiagnosis,
        observation: sourceIndication.observation,
      };
      Object.entries(scalarFieldMap).forEach(([key, value]) => {
        if (
          newIndication.defaultValues?.[key] &&
          value !== undefined &&
          value !== null
        ) {
          newIndication.defaultValues[key].value = value;
        }
      });

      if (newIndication.defaultValues?.patientEmail) {
        let confirmedEmail;
        try {
          ({ value: confirmedEmail } = await this.$prompt(
            "La orden se va a enviar a este email. Podés editarlo antes de continuar.",
            "Email de envío",
            {
              confirmButtonText: "Aceptar",
              cancelButtonText: "Cancelar",
              inputValue: patientEmail,
              inputPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              inputErrorMessage: "El email no tiene un formato válido.",
            }
          ));
        } catch (err) {
          // El doctor canceló: no se agrega la orden repetida.
          return;
        }
        newIndication.defaultValues.patientEmail.value = confirmedEmail.trim();
      }

      this.updateIndication(newIndication);
    },

    saveOrEditText(indication) {
      return indication.lockedAt ? "Editar" : "Guardar";
    },

    getAddIndicationLabel() {
      if (this.form?.type === "images") {
        return "Agregar orden de imagén";
      }

      if (this.form?.type === "lab") {
        return "Agregar otra orden de laboratorio";
      }

      if (this.form?.type === "practices") {
        return "Agregar práctica";
      }

      if (this.form?.type === "certificates") {
        return "Agregar certificado";
      }

      if (this.form?.type === "notes") {
        return "Agregar nota";
      }

      return `Agregar ${
        this.form?.name ? this.form.name.toLowerCase() : "nuevo"
      }`;
    },

    submit(indication) {
      if (this.hasInvalidMedicationQuantity(indication)) {
        this.$message.error("La cantidad debe ser mayor a 0.");
        return;
      }

      const patientEmailValue =
        indication.defaultValues?.patientEmail?.value?.trim();
      if (patientEmailValue) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(patientEmailValue)) {
          this.$message.error(
            "El email del paciente no tiene un formato válido."
          );
          return;
        }
      }

      const typesWithManualDiagnosis = [
        "images",
        "lab",
        "practices",
        "certificates",
      ];
      if (typesWithManualDiagnosis.includes(indication.type)) {
        const dv = indication.defaultValues;
        let diagnosisValue, publicDiagnosisValue;
        if (Array.isArray(dv)) {
          diagnosisValue = dv.find(
            (f) => (f.label || f.name) === "diagnosis"
          )?.value;
          publicDiagnosisValue = dv.find(
            (f) => (f.label || f.name) === "publicDiagnosis"
          )?.value;
        } else {
          diagnosisValue = dv?.diagnosis?.value;
          publicDiagnosisValue = dv?.publicDiagnosis?.value;
        }
        if (!diagnosisValue && !String(publicDiagnosisValue || "").trim()) {
          this.$message.error(
            "Debe completar el diagnóstico o el diagnóstico manual."
          );
          return;
        }
      }

      if (this.mode === "edit") {
        this.submitEdit(indication);
      } else {
        if (indication.lockedAt) {
          indication.lockedAt = null;
        } else {
          indication.lockedAt = new Date();

          if (indication.type === "program" && this.selectedProgram) {
            indication.programData = {
              name: this.selectedProgram.name,
              description: this.selectedProgram.description,
              status: "pending",
            };

            const completedForms = this.getCompletedProgramFormsForIndication(
              indication.key,
              this.selectedProgram._id
            );
            if (completedForms && completedForms.length > 0) {
              indication.completedProgramForms = completedForms;
            }
          }
        }

        this.updateIndication(indication);
      }
    },

    prepareIndicationForEdit(existingIndication) {
      const fields = getIndicationForm(existingIndication.type);
      const defaultValues = {};
      const originalDiagnosis = {};

      for (const field of fields) {
        if (field.inputType === "title") {
          continue;
        }

        const fieldLabel = field.label || field.name;
        const existingValue = existingIndication[fieldLabel];

        if (field.inputType === "diagnosisSelect") {
          if (!existingValue) {
            defaultValues[fieldLabel] = "";
          } else if (typeof existingValue === "string") {
            defaultValues[fieldLabel] = existingValue;
          } else {
            defaultValues[fieldLabel] =
              existingValue.id || existingValue._id || "";
            if (existingValue.id || existingValue._id) {
              originalDiagnosis[existingValue.id || existingValue._id] =
                existingValue;
            }
          }
        } else {
          defaultValues[fieldLabel] =
            existingValue !== undefined ? existingValue : field.value;
        }
      }

      return {
        key: `edit-${existingIndication._id}`,
        _id: existingIndication._id,
        type: existingIndication.type,
        defaultValues: fields.map((f) => ({
          ...f,
          value:
            defaultValues[f.label || f.name] !== undefined
              ? defaultValues[f.label || f.name]
              : f.value,
        })),
        originalDiagnosis,
        createdAt: existingIndication.createdAt,
        lockedAt: null,
      };
    },

    async submitEdit(indication) {
      try {
        this.isPosting = true;

        const payload = this.buildPayloadFromIndication(indication);

        await this.$confirm(
          `¿Está seguro que desea guardar los cambios realizados en la ${
            this.typeName || "indicación"
          }?`,
          "Confirmar cambios",
          {
            confirmButtonText: "Guardar",
            cancelButtonText: "Cancelar",
            type: "question",
          }
        );

        // Filtrar campos inmutables
        const immutableFields = [
          "_id",
          "organization",
          "patient",
          "episode",
          "doctor",
          "type",
          "createdBy",
          "createdAt",
          "files",
          "pdfUrl",
          "archivedAt",
          "cancelledAt",
        ];

        const cleanPayload = {};
        Object.keys(payload).forEach((key) => {
          if (!immutableFields.includes(key)) {
            cleanPayload[key] = payload[key];
          }
        });

        await indicationService.update(indication._id, cleanPayload);

        const indicationTypeName =
          this.typeName ||
          INDICATIONS_TYPES.find((t) => t.type === indication.type)?.name ||
          "indicación";

        this.$message.success(
          `${indicationTypeName} actualizada correctamente`
        );

        this.$emit("saved", {
          ...cleanPayload,
          _id: indication._id,
          type: indication.type,
        });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error al guardar indicación:", error);
          const indicationTypeName =
            this.typeName ||
            INDICATIONS_TYPES.find((t) => t.type === indication.type)?.name ||
            "indicación";
          const errorMessage =
            error.response?.data?.error ||
            `Error al guardar la ${indicationTypeName}`;
          this.$message.error(errorMessage);
        }
      } finally {
        this.isPosting = false;
      }
    },

    buildPayloadFromIndication(indication) {
      const payload = { ...this.existingIndication };
      const fields = getIndicationForm(indication.type);

      for (const field of fields) {
        if (field.inputType === "title") {
          continue;
        }

        const fieldLabel = field.label || field.name;
        const fieldData = indication.defaultValues?.find(
          (f) => (f.label || f.name) === fieldLabel
        );
        let val = fieldData?.value;

        if (field.inputType === "diagnosisSelect") {
          if (!val) {
            val = null;
          } else if (isValidMongoId(val)) {
            const diagnosis = this.getDiagnosisById(val);
            const originalDiagnosis = indication.originalDiagnosis?.[val];
            val = {
              id: val,
              name: diagnosis?.description || originalDiagnosis?.name || "",
            };
          } else if (typeof val === "object" && val.id) {
            val = {
              id: val.id || val._id || null,
              name: val.name || val.description || "",
            };
          }
        }

        payload[fieldLabel] = val;
      }

      return payload;
    },

    cancelEdit() {
      if (this.mode === "edit") {
        this.$emit("cancel");
      }
    },

    canEdit(indication) {
      if (this.mode === "edit") {
        return true;
      }
      return !indication.lockedAt || indication.createdAt;
    },

    revertIndication(indication, index) {
      const previousIndication = this.savedIndications.find(
        (e) => e.key === indication.key
      );

      if (previousIndication) {
        this.$set(this.indications, index, {
          ...cloneDeep(previousIndication),
          lockedAt: new Date(),
        });
        this.updateIndication(this.indications[index]);
      } else {
        this.deleteIndication(indication.key);
      }

      if (this.indications.length < 1) {
        this.isSelected = false;
      }
    },

    getFileName(key = "") {
      return key.split("/").pop();
    },

    getDropdownOptions() {
      return {
        method: "put",
        uploadMultiple: true,
        maxFiles: 8,
        maxFilesize: 5,
        acceptedFiles: "image/*,application/pdf",
      };
    },

    addIndication() {
      const newIndication = this.createIndication();
      this.indications.push(newIndication);
      this.snapshotInitialValues(newIndication);
    },

    createIndication() {
      const newIndications = {
        key: uuidv4(),
        ...cloneDeep(this.form),
      };

      if (newIndications.type === "medications") {
        const quantityField = Object.values(
          newIndications.defaultValues || {}
        ).find(
          (field) =>
            String(field?.friendlyName || "")
              .trim()
              .toLowerCase() === "cantidad"
        );

        if (quantityField && !quantityField.value) {
          quantityField.value = "1";
        }
      }

      const patient =
        (typeof this.appointment?.patient === "object" &&
        this.appointment?.patient !== null
          ? this.appointment.patient
          : null) ||
        this.patient ||
        {};
      const patientEmail = patient?.email || "";
      const patientDni = patient?.governmentId?.number || "";
      const patientSocioNumber =
        patient?.patient?.medicalInsuranceObj?.number ||
        patient?.medicalInsuranceObj?.number ||
        "";
      const patientPlan =
        patient?.patient?.medicalInsuranceObj?.plan ||
        patient?.medicalInsuranceObj?.plan ||
        "";

      if (patientEmail) {
        newIndications.patientEmail = patientEmail;
      }
      if (patientDni) {
        newIndications.patientDni = patientDni;
      }
      if (patientSocioNumber) {
        newIndications.patientSocioNumber = patientSocioNumber;
      }

      if (newIndications.defaultValues?.patientEmail) {
        newIndications.defaultValues.patientEmail.value = patientEmail;
      }
      if (newIndications.defaultValues?.patientDni) {
        newIndications.defaultValues.patientDni.value = patientDni;
      }
      if (newIndications.defaultValues?.patientSocioNumber) {
        newIndications.defaultValues.patientSocioNumber.value =
          patientSocioNumber;
      }
      if (newIndications.defaultValues?.patientPlan) {
        newIndications.defaultValues.patientPlan.value = patientPlan;
      }

      if (this.prefillValues) {
        for (const [key, value] of Object.entries(this.prefillValues)) {
          if (key === "_existingId") {
            newIndications._backendId = value;
            newIndications.lockedAt = new Date();
          } else if (newIndications.defaultValues?.[key] !== undefined) {
            newIndications.defaultValues[key].value = value;
          }
        }
      }

      return newIndications;
    },

    deleteIndication(key) {
      const index = this.indications.findIndex((i) => i.key === key);
      this.updateIndication(this.indications[index], true);
      this.indications.splice(index, 1);
    },

    uploadFilesSuccess(indicationKey) {
      console.log("uploadFilesSuccess", indicationKey);
      this.$bus.$emit("filesUploaded", indicationKey);
    },

    async onFilesUpdated(files, indication) {
      await Vue.nextTick();
      const index = this.indications.findIndex((i) => i.key === indication.key);

      if (index !== -1) {
        this.$set(this.indications, index, {
          ...indication,
          hasFiles: !!files?.length,
        });
      }
    },

    updateDiagnosis(diagnosis, data) {
      this.$set(data, "diagnosis", diagnosis);
    },

    getDiagnosisField(indication) {
      const dv = indication.defaultValues;
      if (!dv) return null;
      if (Array.isArray(dv)) {
        return dv.find((f) => f.inputType === "diagnosisSelect") || null;
      }
      return (
        Object.values(dv).find((f) => f.inputType === "diagnosisSelect") || null
      );
    },

    getPublicDiagnosisField(indication) {
      const dv = indication.defaultValues;
      if (!dv) return null;
      if (Array.isArray(dv)) {
        return dv.find((f) => this.isPublicDiagnosisField(f)) || null;
      }
      return (
        Object.values(dv).find((f) => this.isPublicDiagnosisField(f)) || null
      );
    },

    hasDiagnosisValue(indication) {
      const field = this.getDiagnosisField(indication);
      return !!(field && String(field.value || "").trim());
    },

    hasPublicDiagnosisValue(indication) {
      const field = this.getPublicDiagnosisField(indication);
      return !!(field && String(field.value || "").trim());
    },

    onDiagnosisInput(value) {
      this.diagnosisTouched = true;
      if (value) {
        this.$emit("diagnosis-selected", value);
      }
    },

    onPublicDiagnosisChange(value) {
      this.publicDiagnosisTouched = true;
      if (value) {
        this.$emit("public-diagnosis-selected", value);
      }
    },

    applyExternalDiagnosis(diagnosis) {
      if (this.mode === "edit" || this.diagnosisTouched) return;
      this.indications.forEach((indication) => {
        const field = this.getDiagnosisField(indication);
        if (field) {
          this.$set(field, "value", diagnosis);
          this.updateInitialValue(indication, field, diagnosis);
        }
      });
    },

    applyExternalPublicDiagnosis(value) {
      if (this.mode === "edit" || this.publicDiagnosisTouched) return;
      this.indications.forEach((indication) => {
        const field = this.getPublicDiagnosisField(indication);
        if (field) {
          this.$set(field, "value", value);
          this.updateInitialValue(indication, field, value);
        }
      });
    },

    onFormSelected(form) {
      this.selectedFormForRenderer = { ...form };
      this.isFormRendererVisible = true;
    },

    onFormRendererCompleted(completedForm, indicationKey) {
      this.onFormCompleted(completedForm, indicationKey);
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
    },

    onFormRendererCancel() {
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
      this.isEditingForm = false;
    },

    onProgramSelected(program) {
      this.selectedProgram = program;
      this.programForms = program.initialData || [];
    },

    onProgramFormCompleted(completedForm, indicationKey) {
      if (!this.completedProgramForms[indicationKey]) {
        this.$set(this.completedProgramForms, indicationKey, {});
      }

      if (
        !this.completedProgramForms[indicationKey][this.selectedProgram._id]
      ) {
        this.$set(
          this.completedProgramForms[indicationKey],
          this.selectedProgram._id,
          []
        );
      }

      const existingIndex = this.completedProgramForms[indicationKey][
        this.selectedProgram._id
      ].findIndex((f) => f._id === completedForm._id);

      if (existingIndex >= 0) {
        this.$set(
          this.completedProgramForms[indicationKey][this.selectedProgram._id],
          existingIndex,
          completedForm
        );
      } else {
        this.completedProgramForms[indicationKey][
          this.selectedProgram._id
        ].push(completedForm);
      }

      this.$bus.$emit("program-form-completed", completedForm);
    },

    getCompletedProgramFormsForIndication(indicationKey, programId) {
      return this.completedProgramForms[indicationKey]?.[programId] || [];
    },

    areAllProgramFormsCompleted(indicationKey, programId) {
      const completedForms = this.getCompletedProgramFormsForIndication(
        indicationKey,
        programId
      );
      return (
        completedForms.length === this.programForms.length &&
        this.programForms.length > 0
      );
    },

    isFormCompletedForProgram(formId, indicationKey) {
      if (!this.completedProgramForms[indicationKey] || !this.selectedProgram) {
        return false;
      }

      const completedForms =
        this.completedProgramForms[indicationKey][this.selectedProgram._id] ||
        [];
      return completedForms.some(
        (completedForm) => completedForm._id === formId
      );
    },

    onFormRendererUpdated(updatedForm) {
      const indicationKey = this.editingFormData?.indicationKey;

      if (indicationKey && this.completedForms[indicationKey]) {
        const index = this.completedForms[indicationKey].findIndex(
          (f) => f._id === updatedForm._id
        );

        if (index >= 0) {
          this.$set(this.completedForms[indicationKey], index, updatedForm);
        }
      }

      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
      this.isEditingForm = false;
    },

    onFormCompleted(completedForm, indicationKey) {
      if (!this.completedForms[indicationKey]) {
        this.$set(this.completedForms, indicationKey, []);
      }

      const existingIndex = this.completedForms[indicationKey].findIndex(
        (f) => f._id === completedForm._id
      );

      if (existingIndex >= 0) {
        this.$set(
          this.completedForms[indicationKey],
          existingIndex,
          completedForm
        );
      } else {
        this.completedForms[indicationKey].push(completedForm);
      }

      const indication = this.indications.find((i) => i.key === indicationKey);
      if (indication) {
        if (
          indication.defaultValues &&
          typeof indication.defaultValues === "object" &&
          !Array.isArray(indication.defaultValues)
        ) {
          if (indication.defaultValues.form?.inputType === "formSelect") {
            indication.defaultValues.form.value = "";
          }
        } else if (Array.isArray(indication.defaultValues)) {
          const formField = indication.defaultValues.find(
            (f) => f.inputType === "formSelect"
          );
          if (formField) {
            formField.value = "";
          }
        }
      }
    },

    onFormUpdated(updatedForm, indicationKey) {
      if (this.completedForms[indicationKey]) {
        const existingIndex = this.completedForms[indicationKey].findIndex(
          (f) => f._id === updatedForm._id
        );

        if (existingIndex >= 0) {
          this.$set(
            this.completedForms[indicationKey],
            existingIndex,
            updatedForm
          );
        }
      }
    },

    onEditCompletedForm(completedForm) {
      const indicationKey = Object.keys(this.completedForms).find((key) =>
        this.completedForms[key].some((f) => f._id === completedForm._id)
      );

      const originalForm = this.forms?.find(
        (f) => f._id === completedForm.form?._id
      );

      if (originalForm) {
        this.selectedFormForRenderer = { ...originalForm };
        this.editingFormData = {
          ...completedForm,
          indicationKey,
        };
        this.isEditingForm = true;
        this.isFormRendererVisible = true;
      }
    },

    onRemoveCompletedForm(completedForm, indicationKey) {
      if (this.completedForms[indicationKey]) {
        const index = this.completedForms[indicationKey].findIndex(
          (f) => f._id === completedForm._id
        );
        if (index >= 0) {
          this.completedForms[indicationKey].splice(index, 1);
        }
      }
    },

    getCompletedFormsForIndication(indicationKey) {
      return this.completedForms[indicationKey] || [];
    },

    async getForms() {
      try {
        const formService = await import("@/services/form");
        this.forms = await formService.default.getForms();
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
    },
  },
};
</script>

<template lang="pug">
.indication-box(
  :class="mode === 'edit' ? 'indication-edit-mode' : (isSelected ? 'indication-selected' : 'indication-unselected')"
)
  .indication-box-header(v-if="mode !== 'edit'")
    el-checkbox(v-model="isSelected")
      h3.formName {{ form.name }}
    a.repeat-prescription-link(
      v-if="form.type === 'medications'"
      @click.stop="openRepeatMedicationModal"
    ) Repetir prescripción de medicamento
    a.repeat-prescription-link(
      v-if="form.type === 'lab' || form.type === 'images'"
      @click.stop="openRepeatOrderModal"
    ) Repetir orden
  .indication-box-header(v-else)
    micon(name="edit")
    h3.formName Editar {{ typeName || form.name }}
  span
    ValidationObserver(
      v-slot="{handleSubmit}"
      v-for="(indication, index) in actualIndications"
      :key="indication.key || indication._id"
    )
      form(
        v-show="mode === 'edit' || isSelected"
        @submit.prevent="handleSubmit(() => submit(indication))"
      )
        .modal__row(v-if="mode !== 'edit' && form.allowMultiple")
          fieldset.modal__field
            .header-container
              h1 {{ form.name }} {{ index + 1 }}
              button.button-glass-new(@click="deleteIndication(indication.key)") Quitar
        .modal__row(v-for="question in indication.defaultValues")
          fieldset.modal__field
            ValidationProvider(:name="question.friendlyName" :rules="getQuestionRules(indication, question)", v-slot="{ errors }")
              span(v-if="question.inputType === 'title'")
                label.label.label--title {{ question.friendlyName }}

              span(v-else-if="question.inputType === 'diagnosisSelect'")
                label.label {{ question.friendlyName }}
                DiagnosisSelect(
                  :isDisabled="!canEdit(indication) || hasPublicDiagnosisValue(indication)"
                  v-model="question.value"
                  :key="indication.key + '-' + question.friendlyName"
                  @input="onDiagnosisInput($event)"
                )
              
              span(v-else-if="question.inputType === 'imagesSelect'")
                ImagesSelect(
                  :isDisabled="!canEdit(indication)"
                  v-model="question.value"
                  :key="indication.key + '-' + question.friendlyName"
                )

              span(v-else-if="question.inputType === 'labSelect'")
                LabSelect(
                  :isDisabled="!canEdit(indication)"
                  v-model="question.value"
                  :key="indication.key + '-' + question.friendlyName"
                )

              span(v-else-if="question.inputType === 'medicationSelect'")
                label.label {{ question.friendlyName }}
                MedicationSelect(
                  v-model="question.value"
                  :isDisabled="!canEdit(indication)"
                  :key="indication.key + '-' + question.friendlyName"
                )

              span(v-else-if="question.inputType === 'financiadorSelect'")
                label.label {{ question.friendlyName }}
                FinanciadorSelect(
                  v-model="question.value"
                  :isDisabled="!canEdit(indication) || isDisabledByRecetarioSolidario(indication, question)"
                  :patientInsuranceName="patientInsuranceName"
                  :key="indication.key + '-' + question.friendlyName"
                )

              span(v-else-if="question.inputType === 'multiDate'")
                label.label {{ question.friendlyName }}
                MultiDatePicker(
                  v-model="question.value"
                  :isDisabled="!canEdit(indication)"
                  :key="indication.key + '-' + question.friendlyName"
                )
              span(v-else-if="question.inputType === 'formSelect'")
                label.label {{ question.friendlyName }}
                FormSelect(
                  v-model="question.value"
                  :isDisabled="!canEdit(indication)"
                  :completedForms="getCompletedFormsForIndication(indication.key)"
                  :key="indication.key + '-' + question.friendlyName"
                  @form-selected="onFormSelected"
                  @edit-completed-form="onEditCompletedForm"
                  @remove-completed-form="(form) => onRemoveCompletedForm(form, indication.key)"
                )
                
                FormRenderer(
                  v-if="isFormRendererVisible && selectedFormForRenderer"
                  :selectedForm="selectedFormForRenderer"
                  :patient="currentPatient"
                  :isVisible="isFormRendererVisible"
                  :prefillData="editingFormData"
                  :isEditing="isEditingForm"
                  @form-completed="(completedForm) => onFormRendererCompleted(completedForm, indication.key)"
                  @form-updated="onFormRendererUpdated"
                  @cancel-form="onFormRendererCancel"
                )

              span(v-else-if="question.inputType === 'programSelect'")
                label.label {{ question.friendlyName }}
                ProgramSelect(
                  v-model="question.value"
                  :isDisabled="!canEdit(indication)"
                  :key="indication.key + '-' + question.friendlyName"
                  @program-selected="onProgramSelected"
                )
                
                .program-info(v-if="selectedProgram")
                  .program-details
                    h4.program-name {{ selectedProgram.name }}
                    p.program-description(v-if="selectedProgram.description") {{ selectedProgram.description }}
                    
                    .program-forms(v-if="programForms.length > 0")
                      el-collapse(v-model="activeCollapse" accordion)
                        el-collapse-item(
                          v-for="(form, index) in programForms"
                          :key="form._id"
                          :name="`form-${form._id}`"
                        )
                          template(slot="title")
                            .collapse-title-container
                              span.collapse-title {{ form.name || form.title }}
                              .completion-indicator
                                .completion-circle(:class="{ 'completed': isFormCompletedForProgram(form._id, indication.key) }")
                                  i.el-icon-check(v-if="isFormCompletedForProgram(form._id, indication.key)")
                          FormRenderer(
                            :selectedForm="form"
                            :patient="currentPatient"
                            :isVisible="true"
                            :prefillData="null"
                            :isEditing="false"
                            @form-completed="(completedForm) => onProgramFormCompleted(completedForm, indication.key)"
                          )
                            
                      el-collapse(v-model="activeCollapse" accordion)
                        el-collapse-item(name="prescription")
                          template(slot="title")
                            .collapse-title-container
                              span.collapse-title Prescripción de Medicamentos
                              .completion-indicator
                                .completion-circle(:class="{ 'completed': prescriptionCompleted }")
                                  i.el-icon-check(v-if="prescriptionCompleted")

                          .form-content
                            fieldset.modal__field(v-for="(field, idx) in prescriptionForm" :key="idx")
                              label.label {{ field.friendlyName }}

                              el-input(
                                v-if="isPrescriptionQuantityField(field)"
                                type="text"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                v-model="field.value"
                                :placeholder="field.placeholder"
                                :disabled="prescriptionDisabled"
                                @keydown.native="preventInvalidQuantityKey($event)"
                              )

                              el-input(
                                v-else-if="['input','textarea','number'].includes(field.inputType)"
                                :type="field.inputType === 'textarea' ? 'textarea' : field.inputType"
                                v-model="field.value"
                                :placeholder="field.placeholder"
                                :disabled="prescriptionDisabled || isPrescriptionFieldDisabledByRecetarioSolidario(field)"
                              )

                              DiagnosisSelect(
                                v-else-if="field.inputType === 'diagnosisSelect'"
                                v-model="field.value"
                                :disabled="prescriptionDisabled"
                              )

                              MedicationSelect(
                                v-else-if="field.inputType === 'medicationSelect'"
                                v-model="field.value"
                                :is-program="true"
                                :programName="selectedProgram?.name"
                                :isDisabled="prescriptionDisabled"
                              )

                              el-select(
                                v-else-if="field.inputType === 'select'"
                                v-model="field.value"
                                placeholder="Seleccione una opción"
                                :disabled="prescriptionDisabled"
                              )
                                el-option(
                                  v-for="opt in field.options"
                                  :key="opt.value"
                                  :label="opt.label"
                                  :value="opt.value"
                                )

                              MultiDatePicker(
                                v-else-if="field.inputType === 'multiDate'"
                                v-model="field.value"
                                :disabled="prescriptionDisabled"
                              )

                              el-checkbox(
                                v-else-if="field.inputType === 'checkbox'"
                                v-model="field.value"
                                :disabled="prescriptionDisabled"
                              )

                              span.has-error(v-if="isPrescriptionQuantityField(field) && hasInvalidPrescriptionQuantity()")
                                | La cantidad debe ser mayor a 0.
                              span.has-error(v-if="field.inputType === 'medicationSelect' && prescriptionTouched && hasInvalidPrescriptionMedicationSelection()")
                                | Debe seleccionar un medicamento de la lista.
                              span.has-error(v-if="prescriptionTouched && field.required && !field.value")
                                | Este campo es obligatorio

                            .modal__row
                              fieldset.modal__field
                                button.button-blue-new.long(
                                  type="button"
                                  :disabled="isPosting"
                                  @click="savePrescriptionIndication"
                                ) Guardar Prescripción

              span(v-else-if="question.inputType === 'checkbox'")
                label.label {{ question.friendlyName }}
                el-checkbox(v-model="question.value")

              span(v-else-if="question.inputType === 'select'")
                label.label {{ question.friendlyName }}
                el-select(
                  v-model="question.value"
                  :disabled="!canEdit(indication)"
                  placeholder="Seleccione una opción"
                )
                  el-option(
                    v-for="opt in question.options"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  )

              span(v-else-if="isMedicationQuantityQuestion(indication, question)")
                label.label {{ question.friendlyName }}
                el-input(
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  :disabled="!canEdit(indication)"
                  v-model="question.value"
                  :placeholder="question.placeholder"
                  :key="indication.key + '-' + question.friendlyName"
                  @keydown.native="preventInvalidQuantityKey($event)"
                )

              span(v-else-if="isPublicDiagnosisField(question)")
                label.label {{ question.friendlyName }}
                el-input(
                  :type="question.inputType"
                  :disabled="!canEdit(indication) || hasDiagnosisValue(indication)"
                  v-model="question.value"
                  :placeholder="question.placeholder"
                  :key="indication.key + '-' + question.friendlyName"
                  @change="onPublicDiagnosisChange($event)"
                )

              span(v-else)
                label.label {{ question.friendlyName }}
                el-input(
                  :type="question.inputType"
                  :disabled="!canEdit(indication) || isDisabledByRecetarioSolidario(indication, question)"
                  v-model="question.value"
                  :placeholder="question.placeholder"
                  :key="indication.key + '-' + question.friendlyName"
                )

              span.has-error(v-if="isMedicationQuantityQuestion(indication, question) && hasInvalidMedicationQuantity(indication)")
                | La cantidad debe ser mayor a 0.
              span.has-error(v-else) {{ errors[0] }}
        
        .modal__row
          fieldset.modal__field
            file-upload(
              mode="list"
              v-show="indication.type !== 'program' && indication.type !== 'forms' && !isDoctor"
              :url="uploadMediaEndpoint(indication._id)"
              :disabled="!canEdit(indication)"
              :ref="`fileUpload-${indication.key}`"
              :files="indication.files"
              :dropzone-options="getDropdownOptions()"
              @update:files="(newFiles) => indication.files = newFiles"
              @file-added="(file, files) => onFilesUpdated(files, indication)"
              @file-removed="(file, files) => onFilesUpdated(files, indication)"
              @files-removed="() => onFilesUpdated([], indication)"
              @fileupload-success="() => uploadFilesSuccess(indication.key)"
            )

        .modal__row
          fieldset.modal__field
            button.button-glass-new.long(
              type="button"
              @click="mode === 'edit' ? cancelEdit() : revertIndication(indication, index)"
              :disabled="mode === 'edit' ? false : !canEdit(indication)"
            ) Cancelar
          fieldset.modal__field
            button.button-blue-new.long(
              type="submit"
              :disabled="mode === 'edit' ? isPosting : (indication._id || (selectedProgram && !areAllProgramFormsCompleted(indication.key, selectedProgram._id)))"
            ) {{ mode === 'edit' ? 'Guardar' : saveOrEditText(indication) }}
        
    .modal__row(v-if="mode !== 'edit' && isSelected && form.allowMultiple")
      fieldset.modal__field
        button.button-blue-new.long.withMargin(type="button" @click="addIndication()") {{ getAddIndicationLabel() }}

    repeat-medication-modal(
      v-if="form.type === 'medications'"
      :visible.sync="isRepeatMedicationModalVisible"
      :patient-id="currentPatient && currentPatient._id"
      @select="onRepeatMedicationSelected"
    )
    repeat-order-modal(
      v-if="form.type === 'lab' || form.type === 'images'"
      :visible.sync="isRepeatOrderModalVisible"
      :patient-id="currentPatient && currentPatient._id"
      :type="form.type"
      @select="onRepeatOrderSelected"
    )
</template>

<style lang="scss" scoped>
.indication-box {
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  margin: 10px 0;
}

.indication-selected {
  background-color: #d5dbf366;
}

.repeat-prescription-link {
  color: $primary;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
}

.indication-edit-mode {
  border: solid 2px #d5dbf3;
  border-radius: 10px;
  background-color: #fff;

  .indication-box-header {
    padding: 6px 12px;
    font-weight: 600;
    font-size: 15px;
    border-bottom: 2px solid #d5dbf3;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.indication-box-header {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 48px;

  .formName {
    margin: 0 15px;
    font-weight: 600;
    font-size: 13px;
  }

  .repeat-prescription-link {
    margin-left: auto;
  }

  ::v-deep .el-checkbox__inner {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 2px solid $primary;

    &::after {
      height: 10px;
      left: 6px;
      width: 5px;
    }
  }

  ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: $primary;
    border-color: $primary;
  }
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.withMargin {
  margin: 12px 0;
}

article {
  width: 100%;
}

.location {
  .micon {
    margin-right: 10px;
  }
}

.event-detail-modal {
  h2 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0 0 20px;
  }

  .form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;

    .description {
      word-break: break-word;
      font-weight: bold;
      color: $primary;
      margin-bottom: 10px;
    }

    .response {
      margin-left: 20px;
      display: list-item;
      list-style-type: "→ ";
    }

    .attached-files {
      display: flex;
      flex-wrap: wrap;

      .file {
        width: 100%;
        margin: 0 15px 15px 0;

        &:not(.image) a::before {
          content: "→ ";
        }
      }

      .file.image {
        width: 150px;
        height: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
        box-shadow: 5px 4px 8px -3px rgba(0, 0, 0, 0.47);

        img {
          object-fit: cover;
          height: -webkit-fill-available;
        }
      }
    }
  }

  .view-task {
    padding: 1rem;
  }
}
.label--title {
  margin: 0 0 2px 0;
  font-size: 15px;
  font-weight: 600;
  display: block;
  line-height: 1.2;
}

.program-info {
  margin-top: 15px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.program-details {
  margin-bottom: 20px;
}

.program-name {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.program-description {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.program-forms {
  margin-top: 15px;
}

.form-content {
  padding: 15px;
}

.collapse-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.collapse-title {
  flex: 1;
  font-weight: 500;
  color: #303133;
}

.completion-indicator {
  display: flex;
  align-items: center;
}

.completion-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #dcdfe6;
  margin: 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &.completed {
    background-color: #67c23a;
    border-color: #67c23a;

    i {
      color: #ffffff;
      font-size: 12px;
      font-weight: bold;
    }
  }
}

.program-forms {
  .el-collapse {
    border: none;

    .el-collapse-item {
      margin-bottom: 12px;
      border-radius: 8px;
      overflow: hidden;

      .el-collapse-item__header {
        background-color: #f8f9fa;
        border-bottom: 1px solid #e4e7ed;
        border-radius: 8px 8px 0 0;
        padding: 12px 15px;

        &:hover {
          background-color: #f8f9fa;
        }

        &.is-active {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e4e7ed;
          border-radius: 8px 8px 0 0;
        }
      }

      .el-collapse-item__wrap {
        border: none;
        background-color: #ffffff;

        .el-collapse-item__content {
          padding: 0;
          border-radius: 0 0 8px 8px;
        }
      }
    }
  }
}
</style>
